import React from 'react';
import { Col, Row, Modal } from 'antd';
import dayjs from 'dayjs';
import { useContext } from 'react';
import { AppContext } from 'src/contexts/app.context';
import './ModalSalary.scss';
import { IEmployee } from 'src/types/master_data/employee.type';
import { ISalaryTicket } from 'src/types/hr_ticket/salaryTicket.type';

interface ModalSalaryProps {
  open: boolean;
  onClose: () => void;
  salaryTicket?: ISalaryTicket;
  employeeInfo?: IEmployee;
}

type LayoutItem = {
  label: string;
  order: number;
  single?: boolean;
};
type GroupLayoutItem = {
  label: string;
  order: number;
  group: LayoutItem[];
};
type SalaryTicketItem = LayoutItem | GroupLayoutItem;

const salaryTicketLayout: SalaryTicketItem[] = [
  { label: 'SỐ NGÀY NGHỈ CÓ PHÉP', order: 0 },
  { label: 'SỐ NGÀY NGHỈ KHÔNG PHÉP', order: 1 },
  { label: 'TỔNG GIỜ LÀM', order: 2 },
  { label: 'TỔNG GIỜ LÀM THÊM ĐÃ NHÂN HỆ SỐ', order: 3 },
  { label: 'TỔNG GIỜ LÀM KHOÁN', order: 4, single: true },
  { label: 'SỐ NGÀY CTTN', order: 5 },
  { label: 'TỔNG SỐ NGÀY LTTN', order: 6 },
  { label: 'SỐ NGÀY CTNN', order: 7 },
  { label: 'TỔNG SỐ NGÀY NGHỈ KHÔNG LƯƠNG/VƯỢT PHÉP', order: 8 },
  { label: 'TỔNG THU NHẬP', order: 9, single: true },
  {
    label: 'LƯƠNG VÀ KPI',
    order: -1,
    group: [
      { label: 'LƯƠNG CỐ ĐỊNH (lương cơ bản + phụ cấp)', order: 10 },
      { label: 'LƯƠNG KPI VÀ CÁC KHOẢN THƯỞNG', order: 11 },
      { label: 'KHOÁN', order: 12 }
    ]
  },
  {
    label: 'CÔNG TÁC',
    order: -2,
    group: [
      { label: 'CP LT NGOÀI NƯỚC', order: 13 },
      { label: 'CƠM CT NƯỚC NGOÀI', order: 14 },
      { label: 'CP LT TRONG NƯỚC', order: 15 },
      { label: 'CƠM CT TRONG NƯỚC', order: 16 },
      { label: 'XĂNG XE', order: 17 }
    ]
  },
  { label: 'TIỀN XẾP LOẠI', order: 18, single: true },
  { label: 'LÀM THÊM GIỜ/LỄ/TẾT', order: 19, single: true },
  { label: 'TIỀN CƠM TĂNG CA', order: 20, single: true },
  { label: 'CƠM TRƯA', order: 21 },
  { label: 'TRỪ NGHỈ', order: 22 },
  { label: 'CỘNG KHÁC', order: 23 },
  { label: 'TRỪ KHÁC', order: 24 },
  { label: 'TRỪ BẢO HIỂM', order: 25 },
  { label: 'TRỪ CÔNG ĐOÀN', order: 26 },
  { label: 'TRỪ THUẾ TNCN', order: 27, single: true },
  { label: 'TRỪ MƯỢN', order: 28, single: true },
  { label: 'TRỪ QUỸ THIÊN TAI', order: 29, single: true },
  { label: 'TRUY THU BHYT', order: 30, single: true },
  { label: 'TIỀN HOÀN QTT TNCN', order: 31, single: true },
  { label: 'CỘNG CÔNG ĐOÀN, ỐM…', order: 32, single: true },
  { label: 'TRỪ ỨNG LƯƠNG (Nếu có):', order: 33, single: true },
  { label: 'THƯỞNG THÁNG 13:', order: 34 },
  { label: 'THƯỞNG KHÁC (Nếu có):', order: 35 },

  { label: 'THU NHẬP THUẦN', order: 36 },
  { label: 'TIỀN MẶT', order: 37 }
];

const bonusGroup: GroupLayoutItem = {
  label: 'BONUS',
  order: 900,
  group: [
    { label: 'THƯỞNG', order: 901 },
    { label: 'CK THƯỞNG LẦN 1', order: 902 },
    { label: 'CK THƯỞNG LẦN 2', order: 903 },
    { label: 'TĂNG LƯƠNG', order: 904 }
  ]
};

export default function ModalSalary({ open, onClose, salaryTicket, employeeInfo }: ModalSalaryProps) {
  const { userInfo } = useContext(AppContext);
  const prevMonth = dayjs().subtract(1, 'month').format('MM/YYYY');

  const valueMap: Map<number, string> = new Map(
    salaryTicket?.details?.map((item: any) => {
      const value = Number(item.value);
      const formattedValue = value === 0 ? '-' : value.toLocaleString('vi-VN');
      return [item.order, formattedValue];
    })
  );
  // check bonus group
  const hasBonus = bonusGroup.group.some((item) => valueMap.has(item.order) && valueMap.get(item.order) !== '-');
  let layout: SalaryTicketItem[] = [...salaryTicketLayout];

  if (hasBonus) {
    const insertIndex = layout.findIndex((item) => 'order' in item && item.order === 36);
    if (insertIndex !== -1) {
      layout.splice(insertIndex, 0, bonusGroup); // chèn trước order 37
    }
  }

  const customOrders = [9, 37];

  // Tách layout theo 2 cột
  const leftLayout = layout.filter((item) => item.order < 19);
  const rightLayout = layout.filter((item) => item.order >= 19);

  const buildRows = (layout: SalaryTicketItem[]) => {
    const rows: LayoutItem[][] = [];
    const queue = [...layout];
    while (queue.length > 0) {
      const first = queue.shift();
      if (!first) break;
      if ('group' in first) {
        rows.push([first]);
      } else if (first.single) {
        rows.push([first]);
      } else {
        const second = 'group' in (queue[0] || {}) || (queue[0] as LayoutItem)?.single ? undefined : queue.shift();
        rows.push([first, second!]);
      }
    }
    return rows;
  };

  const leftRows = buildRows(leftLayout);
  const rightRows = buildRows(rightLayout);

  return (
    <Modal
      className='modal-salary'
      title='PHIẾU LƯƠNG'
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={1500}
    >
      <div style={{ height: '80vh', overflowY: 'auto', padding: '24px' }}>
        <Row gutter={24}>
          {[leftRows, rightRows].map((rows, colIndex) => (
            <Col
              key={colIndex}
              span={12}
              style={{
                paddingRight: colIndex === 0 ? 12 : 0, // cột 1 bên phải cách đường ngăn
                paddingLeft: colIndex === 1 ? 24 : 0, // cột 2 bên trái cách đường ngăn
                borderRight: colIndex === 0 ? '1px dashed #ccc' : 'none' // đường ngăn giữa 2 cột
              }}
            >
              {/* Thông tin user */}
              {colIndex === 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ marginBottom: 16 }}>
                        <span>THÁNG:</span> <span style={{ fontWeight: 600 }}>{prevMonth}</span>
                      </div>
                      <div>
                        <span>MÃ NHÂN VIÊN:</span>{' '}
                        <span style={{ fontWeight: 600 }}>{employeeInfo?.code ?? userInfo?.code}</span>
                      </div>
                    </Col>
                    <Col span={12} style={{ paddingLeft: 0 }}>
                      <div style={{ marginBottom: 16 }}>
                        <span>PHÒNG/BỘ PHẬN:</span>{' '}
                        <span style={{ fontWeight: 600 }}>
                          {String(employeeInfo?.department?.name ?? userInfo?.department ?? '')}
                        </span>
                      </div>
                      <div>
                        <span>HỌ TÊN:</span>{' '}
                        <span style={{ fontWeight: 600 }}>{employeeInfo?.fullName ?? userInfo?.fullName}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* render rows trong mỗi cột */}
              {rows.map((row, i) => (
                <Row key={i} gutter={[16, 16]} className='salary-slip-row' style={{ marginBottom: 16 }}>
                  {row.map((item, j) => (
                    <React.Fragment key={`col-${colIndex}-row-${i}-item-${j}`}>
                      {'group' in item ? (
                        <Col span={12} className='salary-slip-item'>
                          <div className='label'>{item.label}</div>
                          <ul>
                            {(item as GroupLayoutItem).group.map((subItem) => (
                              <li key={subItem.order} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{`+ ${subItem.label}`}</span>
                                <span>{valueMap.get(subItem.order) ?? '-'}</span>
                              </li>
                            ))}
                          </ul>
                        </Col>
                      ) : (
                        <Col span={12} className='salary-slip-item'>
                          <div className='salary-slip-item-content'>
                            <div
                              className='label'
                              style={{
                                textDecoration: customOrders.includes(item.order) ? 'underline' : 'none',
                                fontWeight: customOrders.includes(item.order) ? 'bold' : 'normal'
                              }}
                            >
                              {item.label}
                            </div>
                            <div
                              className='value'
                              style={{
                                fontWeight: customOrders.includes(item.order) ? 'bold' : 'normal'
                              }}
                            >
                              {valueMap.get(item.order) ?? '-'}
                            </div>
                          </div>
                        </Col>
                      )}
                    </React.Fragment>
                  ))}
                </Row>
              ))}
            </Col>
          ))}
        </Row>
      </div>
    </Modal>
  );
}
